import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { supabaseEnabled } from '../../lib/supabase';
import { useAuthStore } from '../../state/authStore';
import { Smartphone } from 'lucide-react';

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}

/**
 * Renders the "or continue with…" block for login + signup screens.
 * Silently renders nothing when Supabase is not configured for the frontend
 * (REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY unset). That keeps
 * existing Mongo-only dev deployments untouched.
 *
 * Props:
 *   - mode: 'login' | 'signup' (affects labels + which secondary links show)
 */
export default function SupabaseAuthOptions({ mode = 'login' }) {
  const navigate = useNavigate();
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!supabaseEnabled) return null;

  const handleGoogle = async () => {
    setError('');
    setBusy(true);
    const result = await loginWithGoogle();
    if (!result.success) {
      setBusy(false);
      setError(result.error || 'Could not start Google sign-in.');
    }
    // On success the tab redirects to Google; no further handling needed.
  };

  return (
    <div className="space-y-3">
      <div className="relative py-1">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-800" />
        </div>
        <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
          <span className="px-2 bg-gray-900 text-gray-500">or {mode === 'signup' ? 'sign up' : 'continue'} with</span>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-400 text-center" role="alert">{error}</p>
      )}

      <Button
        type="button"
        variant="outline"
        onClick={handleGoogle}
        disabled={busy}
        className="w-full bg-white hover:bg-gray-100 text-gray-900 border-gray-300 flex items-center justify-center gap-2"
      >
        <GoogleMark />
        <span className="font-medium">{busy ? 'Redirecting…' : 'Continue with Google'}</span>
      </Button>

      <Button
        type="button"
        variant="outline"
        onClick={() => navigate('/login/phone')}
        className="w-full border-gray-700 text-gray-200 hover:bg-gray-800 flex items-center justify-center gap-2"
      >
        <Smartphone className="w-4 h-4" />
        <span>{mode === 'signup' ? 'Sign up with phone' : 'Sign in with phone'}</span>
      </Button>

      {mode === 'login' && (
        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="text-xs text-gray-400 hover:text-emerald-400 hover:underline"
          >
            Forgot your password?
          </button>
        </div>
      )}
    </div>
  );
}
